import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Shield, Users, Trophy, TrendingUp, Edit, Award, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { gamificationService, authService } from '../services';
import PageContainer from '../components/PageContainer';
import EmptyState from '../components/EmptyState';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/Loading';
import EditProfileModal from '../components/EditProfileModal';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    loadUserStats();
  }, []);

  const loadUserStats = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await gamificationService.getUserStats();
      setStats(response);
    } catch (err) {
      console.error('Error loading user stats:', err);
      setError(err.response?.data?.error || 'Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (profileData) => {
    try {
      setUpdating(true);
      setError('');
      
      // Separar dados de perfil e senha
      const { currentPassword, newPassword, confirmPassword, ...profileInfo } = profileData;
      
      // Atualizar informações básicas do perfil
      if (profileInfo.name || profileInfo.phone) {
        const response = await authService.updateProfile(profileInfo);
        updateUser(response.user);
      }
      
      // Atualizar senha se fornecida
      if (newPassword && currentPassword) {
        await authService.changePassword({
          currentPassword,
          newPassword,
          confirmPassword
        });
      }
      
      setShowEditModal(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.error || 'Erro ao atualizar perfil');
    } finally {
      setUpdating(false);
    }
  };

  if (loading && !stats) {
    return (
      <PageContainer title="Meu Perfil" description="Carregando...">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner text="Carregando perfil..." />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      icon={User}
      title="Meu Perfil"
      description="Gerencie suas informações pessoais e acompanhe suas estatísticas"
      actions={
        <Button onClick={() => setShowEditModal(true)}>
          <Edit className="w-4 h-4 mr-2" />
          Editar Perfil
        </Button>
      }
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card variant="glass" className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent"></div>
            <div className="relative p-6 text-center">
              <Users className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
              <p className="text-sm text-gray-400 mb-1">Personagens</p>
              <h3 className="text-3xl font-bold text-white">{stats?.characters?.count || 0}</h3>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card variant="glass" className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent"></div>
            <div className="relative p-6 text-center">
              <Calendar className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <p className="text-sm text-gray-400 mb-1">Eventos</p>
              <h3 className="text-3xl font-bold text-white">{stats?.eventsParticipated || 0}</h3>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card variant="glass" className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent"></div>
            <div className="relative p-6 text-center">
              <Trophy className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-sm text-gray-400 mb-1">Pontos</p>
              <h3 className="text-3xl font-bold text-white">{stats?.totalPoints?.toLocaleString() || 0}</h3>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Card variant="glass" className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent"></div>
            <div className="relative p-6 text-center">
              <TrendingUp className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-gray-400 mb-1">Ranking</p>
              <h3 className="text-3xl font-bold text-white">
                {stats?.userRanking ? `#${stats.userRanking}` : '-'}
              </h3>
            </div>
          </Card>
        </motion.div>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/20 text-red-400 px-6 py-4 rounded-xl mb-6"
        >
          <div className="flex items-center justify-between">
            <p className="font-medium">{error}</p>
            <Button variant="outline" size="sm" onClick={loadUserStats}>
              Tentar novamente
            </Button>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Personal Info Card */}
        <div className="lg:col-span-2">
          <Card variant="glass">
            <div className="p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <User className="w-6 h-6 text-cyan-400" />
                Informações Pessoais
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Nome
                    </label>
                    <p className="text-lg font-semibold text-white">{user?.name}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Email
                    </label>
                    <p className="text-lg font-semibold text-white">{user?.email}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Permissão
                    </label>
                    <span className={`inline-block px-3 py-1 rounded-lg text-sm font-medium ${
                      user?.role === 'ADMIN' 
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    }`}>
                      {user?.role === 'ADMIN' ? 'Administrador' : 'Membro'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Best Character Card */}
        <div>
          {stats?.characters?.best ? (
            <Card variant="glass">
              <div className="p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Award className="w-6 h-6 text-amber-400" />
                  Melhor Personagem
                </h2>
                
                <div className="relative p-6 rounded-xl overflow-hidden" style={{
                  background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)',
                  border: '1px solid rgba(251, 191, 36, 0.2)'
                }}>
                  <div className="text-center">
                    <div className="text-5xl mb-4">🏆</div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {stats.characters.best.name}
                    </h3>
                    <span className="inline-block px-3 py-1 rounded-lg bg-amber-500/20 text-amber-400 text-sm font-medium border border-amber-500/30 mb-4">
                      {stats.characters.best.class}
                    </span>
                    
                    <div className="grid grid-cols-2 gap-4 mt-6">
                      <div className="p-3 bg-white/10 rounded-lg">
                        <p className="text-sm text-gray-400 mb-1">Nível</p>
                        <p className="text-2xl font-bold text-white">{stats.characters.best.level}</p>
                      </div>
                      <div className="p-3 bg-white/10 rounded-lg">
                        <p className="text-sm text-gray-400 mb-1">Pontos</p>
                        <p className="text-2xl font-bold text-amber-400">
                          {stats.characters.best.totalPoints.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ) : (
            <Card variant="glass">
              <div className="p-6">
                <EmptyState
                  icon={Users}
                  title="Nenhum personagem"
                  description="Crie seu primeiro personagem para começar sua jornada!"
                />
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdateProfile}
        user={user}
        loading={updating}
      />
    </PageContainer>
  );
};

export default Profile;